import { Select as SelectPrimitive } from '@base-ui/react/select';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { CheckmarkIcon } from '../../components/svgs/icons/CheckmarkIcon';
import { ChevronDown } from '../../components/svgs/icons/ChevronDown';
import { ChevronUp } from '../../components/svgs/icons/ChevronUp';
import { brandPrimary } from '../../styles/colors';
import { bs } from '../../styles/styles';
import { s } from './styles';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

function SelectValue({
	placeholder,
	...props
}: React.ComponentProps<typeof SelectPrimitive.Value> & {
	placeholder?: string;
}) {
	if (!placeholder) {
		return <SelectPrimitive.Value data-slot='select-value' {...props} />;
	}
	return (
		<SelectPrimitive.Value
			className={css(bs.textSm)}
			render={(_, { value }) => {
				if (value) {
					return <SelectPrimitive.Value data-slot='select-value' {...props} />;
				}
				return (
					<span data-slot='select-value' className={css(bs.textDarkGrayFontColor)}>
						{placeholder}
					</span>
				);
			}}
			{...props}
		/>
	);
}

const SelectTrigger = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Trigger>,
	Exclude<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>, 'className'> & {
		styles?: StyleDeclarationValue;
	}
>(({ styles, children, ...props }, ref) => (
	<SelectPrimitive.Trigger data-slot='select-trigger' ref={ref} className={css(s.trigger, styles)} {...props}>
		{children}
		<SelectPrimitive.Icon render={() => <ChevronDown className={css(s.triggerChevron)} />} />
	</SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.ScrollUpArrow>,
	Exclude<React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpArrow>, 'className'> & {
		styles?: StyleDeclarationValue;
	}
>(({ styles, ...props }, ref) => (
	<SelectPrimitive.ScrollUpArrow ref={ref} className={css(s.scrollUpButton, styles)} {...props}>
		<ChevronUp className={css(s.scrollButtonChevron)} />
	</SelectPrimitive.ScrollUpArrow>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpArrow.displayName;

const SelectScrollDownButton = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.ScrollDownArrow>,
	Exclude<React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownArrow>, 'className'> & {
		styles?: StyleDeclarationValue;
	}
>(({ styles, ...props }, ref) => (
	<SelectPrimitive.ScrollDownArrow ref={ref} className={css(s.scrollDownButton, styles)} {...props}>
		<ChevronDown className={css(s.scrollButtonChevron)} />
	</SelectPrimitive.ScrollDownArrow>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownArrow.displayName;

const SelectContent = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Popup>,
	Exclude<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Popup>, 'className'> & {
		styles?: StyleDeclarationValue;
		position?: 'popper' | 'item-aligned';
		sideOffset?: SelectPrimitive.Positioner.Props['sideOffset'];
		side?: SelectPrimitive.Positioner.Props['side'];
		align?: SelectPrimitive.Positioner.Props['align'];
		alignOffset?: SelectPrimitive.Positioner.Props['alignOffset'];
		/**
		 * Where to portal the popup. Defaults to `<body>`. Pass the host element when the Select sits
		 * inside another dismissable layer — a Radix popover treats a body-portaled popup as an outside
		 * click and closes itself the moment an item is picked.
		 */
		container?: SelectPrimitive.Portal.Props['container'];
	}
>(
	(
		{
			styles,
			children,
			position = 'popper',
			side = 'bottom',
			sideOffset = 2,
			align = 'start',
			alignOffset = 0,
			container,
			...props
		},
		ref
	) => (
		<SelectPrimitive.Portal container={container}>
			<SelectPrimitive.Positioner
				alignItemWithTrigger={position === 'item-aligned'}
				side={side}
				sideOffset={sideOffset}
				align={align}
				alignOffset={alignOffset}
				className={css(s.positioner)}
			>
				<SelectPrimitive.Popup ref={ref} className={css(s.selectContent, styles)} {...props}>
					<SelectScrollUpButton />
					<SelectPrimitive.List className={css(s.selectList)}>{children}</SelectPrimitive.List>
					<SelectScrollDownButton />
				</SelectPrimitive.Popup>
			</SelectPrimitive.Positioner>
		</SelectPrimitive.Portal>
	)
);
SelectContent.displayName = SelectPrimitive.Popup.displayName;

const SelectGroupLabel = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.GroupLabel>,
	Exclude<React.ComponentPropsWithoutRef<typeof SelectPrimitive.GroupLabel>, 'className'> & {
		styles?: StyleDeclarationValue;
	}
>(({ styles, ...props }, ref) => (
	<SelectPrimitive.GroupLabel ref={ref} className={css(s.selectLabel, styles)} {...props} />
));
SelectGroupLabel.displayName = SelectPrimitive.GroupLabel.displayName;

const SelectItem = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Item>,
	Exclude<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>, 'className'> & {
		styles?: StyleDeclarationValue;
		/**
		 * Secondary text shown at the right of the open row, such as a count beside a name. It is a
		 * sibling of the item's text rather than part of it, so the collapsed trigger keeps showing the
		 * label alone.
		 */
		hint?: React.ReactNode;
		hintStyles?: StyleDeclarationValue;
	}
>(({ styles, hint, hintStyles, children, ...props }, ref) => (
	<SelectPrimitive.Item ref={ref} className={css(s.selectItem, styles)} {...props}>
		<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
		{hint !== undefined && hint !== null && <span className={css(s.selectItemHint, hintStyles)}>{hint}</span>}
	</SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectCheckboxItem = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Item>,
	Exclude<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>, 'className'> & { styles?: StyleDeclarationValue }
>(({ styles, children, ...props }, ref) => (
	<SelectPrimitive.Item ref={ref} className={css(s.selectCheckboxItem, styles)} {...props}>
		<div className={css(s.checkboxItemCheckWrapper)}>
			<SelectPrimitive.ItemIndicator className={css(s.checkBoxItemCheckIndicator)}>
				<CheckmarkIcon fillColor={brandPrimary} width={8} height={8} />
			</SelectPrimitive.ItemIndicator>
		</div>
		<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
	</SelectPrimitive.Item>
));
SelectCheckboxItem.displayName = 'SelectCheckboxItem';

const SelectSeparator = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Separator>,
	Exclude<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>, 'className'> & {
		styles?: StyleDeclarationValue;
	}
>(({ styles, ...props }, ref) => (
	<SelectPrimitive.Separator ref={ref} className={css(s.selectSeparator, styles)} {...props} />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
	Select,
	SelectCheckboxItem,
	SelectContent,
	SelectGroup,
	SelectGroupLabel,
	SelectItem,
	SelectScrollDownButton,
	SelectScrollUpButton,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
};
